import type { PaymentSessionResult } from '#root/api/clients/cart/data-contracts'
import type { Order } from '#types/components/checkout/order'
import type {
  ShopPayContactField,
  ShopPayMoney,
  ShopPayPaymentCompletePayload,
  ShopPayPaymentRequest,
  ShopPayPaymentRequestSession,
  ShopPaySessionEventMap,
  ShopPayUserError
} from './types'
import { ApiError } from '#root/api/util/error'

const SHOP_PAY_SDK_URL = 'https://cdn.shopify.com/shopifycloud/shop-js/shop-pay-payment-request.js'
// Shop Pay supports one active SDK session across all rendered payment surfaces.
let activeShopPaySession: ShopPayPaymentRequestSession | undefined
// Checkout ownership survives SDK close while cancellation or navigation is pending.
let activeShopPayCheckoutOwner: symbol | undefined

interface ShopPayPaymentAttempt {
  orderNumber?: string
  paymentInstrumentId?: string
  lastError?: ShopPaySessionEventMap['paymentattemptfailed']['error']
  paymentCompleteReceived: boolean
  confirmation?: Promise<void>
  completion?: Promise<void>
  failure?: Promise<void>
}

const toMoney = (amount: number | undefined, currencyCode: string): ShopPayMoney => ({
  amount: (amount || 0).toFixed(2),
  currencyCode
})

const toCartAddress = (address: ShopPayContactField) => ({
  addressLine1: address.address1,
  addressLine2: address.address2 || '',
  city: address.city,
  country: address.countryCode,
  email: address.email || '',
  firstName: address.firstName || '',
  lastName: address.lastName,
  phone: address.phone || '',
  postalCode: address.postalCode || '',
  province: address.provinceCode || ''
})

/**
 * Integrates the Shop Pay payment-request SDK with Canvas cart and checkout APIs.
 *
 * Loads and configures the SDK only when the surface is eligible and credentials are valid,
 * keeps the SDK's single active session owned by one surface at a time,
 * builds the wallet request from the live cart, and completes Shopify session, shipping,
 * confirmation, failure, and receipt events through the existing Cart API.
 *
 * @param context - Shop Pay surface. Standard checkout requires its independent rollout flag;
 * express surfaces retain their separate eligibility rules.
 * @param options - Synchronous checkout checks, preserving the original click for the SDK popup.
 * @param options.beforeProceed - Validates invoice and consent without awaiting shipping preparation.
 * @returns Eligibility state and lifecycle methods for rendering and removing the SDK button.
 */
export default (
  context: 'cart' | 'checkout' | 'checkout-payment' | 'minicart' | 'quickshop',
  { beforeProceed }: { beforeProceed?: () => boolean } = {}
) => {
  const api = useApi()
  const auth = useAuthStore()
  const cart = useCartStore()
  const checkout = useCheckoutStore()
  const locale = useLocale()
  const recaptcha = useRecaptcha()
  const forterSessionToken = useState<string>('forterSessionToken')
  const { $deviceFingerPrint, $feature, $t } = useNuxtApp()
  const { showShopPayOnCart } = useExpressPaymentFeatures()
  const { load: loadScript } = useScript(SHOP_PAY_SDK_URL, { trigger: 'manual' })
  const countryCode = getCountryCode(locale)
  const shippingCountryError = replaceAll($t.shopPayShippingCountryError, {
    country: new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode) || countryCode
  })
  const deliveryDateFormatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    weekday: 'long'
  })
  const deliveryRangeFormatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    weekday: 'short'
  })

  let session: ShopPayPaymentRequestSession | undefined
  let initializationGeneration = 0
  const isLoading = ref(false)
  let buttonElement: Element | undefined
  let checkoutValidationSucceeded = false
  let orderNavigationPending = false

  const shopPayConfig = computed(() => $feature.configShopPay)
  const pickupShippingMethod = computed(() => cart.shippingMethods.find(isPickupOrSts))
  const isPickupOrder = computed(() => !!pickupShippingMethod.value
    && cart.shippingMethods.every((method) => isPickupOrSts(method) || isDigitalDelivery(method)))
  const hasValidConfig = computed(() =>
    Number.isInteger(shopPayConfig.value?.shopId)
    && shopPayConfig.value.shopId > 0
    && !!shopPayConfig.value.clientId
  )

  const isEligible = computed(() =>
    hasValidConfig.value
    && (
      (context === 'checkout-payment' && $feature.showShopPayOnStandardCheckout === true)
      || (
        ['cart', 'checkout'].includes(context)
        && (context !== 'cart' || showShopPayOnCart.value)
        && !!cart.totalItems
        && !cart.hasCustomsProduct
        && !cart.hasCollectionPointItems
        && !cart.hasPickupItems
        && !auth.inStoreEmployeeLoggedIn
      )
    )
  )

  const formatDeliveryExpectation = (deliveryTime: string, code: string) => {
    if (!deliveryTime) return undefined
    if (code === 'prime') return deliveryTime

    const dates = deliveryTime.split(',').map((value) => new Date(value.trim()))
    if ($feature.tempShowEddDateRange && dates.length === 2 && dates.every((date) => !Number.isNaN(date.getTime()))) {
      return replaceAll($t.estDelivery, {
        expectedTime: dates.map((date) => deliveryRangeFormatter.format(date)).join(' - ')
      })
    }

    const date = new Date(deliveryTime)
    if (!Number.isNaN(date.getTime())) {
      return replaceAll($t.estimatedDelivery, { expectedTime: deliveryDateFormatter.format(date) })
    }
    return code === 'nextday' ? $t.shippingArrivingTomorrow : `${$t.shipsIn} ${deliveryTime}`
  }

  const getPaymentRequest = (): ShopPayPaymentRequest => {
    const currencyCode = cart.currency.toUpperCase()
    const pickupMethod = isPickupOrder.value ? pickupShippingMethod.value : undefined
    const pickupAddress = getFormattedAddress(pickupMethod?.address)
    const shippingGroup = cart.defaultShippingMethod
    const selectedShippingMethod = shippingGroup
      ? getApplicableShippingMethod(shippingGroup) || shippingGroup.applicableShippingMethods?.[0]
      : undefined
    const deliveryFee = cart.totals.enforcedShippingFee || 0
    const originalShippingPrice = (selectedShippingMethod?.price.amount ?? cart.totals.shipping) + deliveryFee
    const finalShippingPrice = (selectedShippingMethod?.price.finalAmount
      ?? selectedShippingMethod?.price.amount ?? cart.totals.shipping) + deliveryFee

    const shippingDiscounts = (shippingGroup?.shippingPromotions || []).map(({ itemText, price }) => ({
      amount: toMoney(Math.abs(price), currencyCode),
      label: itemText
    }))

    const discounts: ShopPayPaymentRequest['discounts'] = cart.totals.totalDiscount
      ? [{
          amount: toMoney(Math.abs(cart.totals.totalDiscount), currencyCode),
          label: $t.discountItem
        }]
      : []

    for (const { amount, payment_method_id } of cart.paymentInstruments) {
      if (amount <= 0) continue
      if (!['GIFT_CARD', 'LOYALTY_POINTS', 'REWARD_CARD', 'REWARD_CODE'].includes(payment_method_id)) continue
      discounts.push({
        amount: toMoney(amount, currencyCode),
        label: payment_method_id === 'GIFT_CARD'
          ? $t.giftCard
          : payment_method_id === 'LOYALTY_POINTS' ? $t.loyaltyCreditsRedeemed : $t.rewardCard
      })
    }

    const deliveryMethods = (shippingGroup?.applicableShippingMethods || []).map((method) => ({
      amount: toMoney((method.price.finalAmount ?? method.price.amount) + deliveryFee, currencyCode),
      code: method.code,
      deliveryExpectationLabel: formatDeliveryExpectation(method.deliveryTime, method.code),
      label: method.label
    }))

    const shippingLines = selectedShippingMethod
      ? [{
          amount: toMoney(finalShippingPrice, currencyCode),
          code: selectedShippingMethod.code,
          label: selectedShippingMethod.label
        }]
      : []

    return {
      deliveryMethods: pickupMethod ? [] : deliveryMethods,
      discountCodes: cart.couponItems.filter(({ valid }) => valid).map(({ code }) => code),
      discounts,
      lineItems: cart.items.map((item) => {
        const finalLinePrice = Math.min(
          item.price.rowTotal,
          item.price.priceAfterItemDiscount,
          item.price.priceAfterOrderDiscount
        )

        return {
          finalItemPrice: toMoney(finalLinePrice / item.qty, currencyCode),
          finalLinePrice: toMoney(finalLinePrice, currencyCode),
          image: item.productImageURL
            ? { alt: item.name, url: item.productImageURL }
            : undefined,
          label: item.name,
          originalItemPrice: toMoney(item.price.original, currencyCode),
          originalLinePrice: toMoney(item.price.originalRowTotal, currencyCode),
          quantity: item.qty,
          requiresShipping: !item.isDigitalProduct,
          sku: item.upc || item.productId
        }
      }),
      locale: locale.split('-')[0],
      presentmentCurrency: currencyCode,
      pickupLocations: pickupMethod
        ? [{
            amount: toMoney(pickupMethod.cost, currencyCode),
            code: pickupMethod.storeId || pickupMethod.shippingId,
            detail: [pickupAddress?.addressLine1, pickupAddress?.addressLine2, pickupAddress?.cityState].filter(Boolean).join(', '),
            label: pickupMethod.storeName || $t.pickupInStore
          }]
        : [],
      selectedDeliveryMethodType: pickupMethod ? 'PICKUP' : 'SHIPPING',
      shippingLines: pickupMethod ? [] : shippingLines,
      subtotal: toMoney(cart.totals.itemTotal, currencyCode),
      supportedDeliveryMethodTypes: [pickupMethod ? 'PICKUP' : 'SHIPPING'],
      total: toMoney(cart.totals.remainingToPay ?? cart.totals.total, currencyCode),
      totalShippingPrice: pickupMethod
        ? undefined
        : {
            discounts: shippingDiscounts,
            finalTotal: toMoney(finalShippingPrice, currencyCode),
            originalTotal: toMoney(originalShippingPrice, currencyCode)
          },
      totalTax: toMoney(cart.totals.taxAmount ?? cart.totals.tax, currencyCode)
    }
  }

  const generalError = (message: string = $t.paymentAuthenticationError): ShopPayUserError[] => [{
    message,
    type: 'generalError'
  }]

  const buildCurrentPaymentRequest = () => window.ShopPay!.PaymentRequest.build({
    ...session?.paymentRequest,
    ...getPaymentRequest()
  })

  const buildRejectedShippingRequest = () => window.ShopPay!.PaymentRequest.build({
    ...buildCurrentPaymentRequest(),
    deliveryMethods: []
  })

  const closeActiveSession = () => {
    if (!isClient() || !activeShopPaySession) return

    activeShopPaySession.close()
    activeShopPaySession = undefined
  }

  const handleSessionRequested = async (ownsSession: () => boolean) => {
    try {
      if (!isEligible.value || (beforeProceed && !checkoutValidationSucceeded)) {
        session?.completeSessionRequest({
          checkoutUrl: '',
          errors: generalError(),
          sourceIdentifier: '',
          token: ''
        })
        return
      }
      checkoutValidationSucceeded = false
      isLoading.value = true

      const response = await cart.postPaymentInstruments('SHOP_PAY')
      if (!ownsSession()) return
      const paymentInstrument = response?.payment_instruments?.find(
        (instrument: Record<string, unknown>) => instrument.payment_method_id === 'SHOP_PAY'
      )
      const paymentSessionResult = paymentInstrument?.paymentSessionResult as PaymentSessionResult | undefined

      if (!paymentSessionResult?.checkoutUrl || !paymentSessionResult.sourceIdentifier || !paymentSessionResult.token) {
        throw new Error($t.paymentAuthenticationError)
      }

      session?.completeSessionRequest({
        checkoutUrl: paymentSessionResult.checkoutUrl,
        sourceIdentifier: paymentSessionResult.sourceIdentifier,
        token: paymentSessionResult.token,
        updatedPaymentRequest: buildCurrentPaymentRequest()
      })
    }
    catch (err) {
      if (!ownsSession()) return
      const message = err instanceof Error ? err.message : $t.paymentAuthenticationError
      cart.setNotification('error', message, 'top')
      session?.completeSessionRequest({
        checkoutUrl: '',
        errors: generalError(message),
        sourceIdentifier: '',
        token: ''
      })
    }
    finally {
      if (ownsSession()) {
        isLoading.value = false
      }
    }
  }

  const handleShippingAddressChanged = async (
    address: ShopPayContactField,
    ownsSession: () => boolean
  ) => {
    if (!isEligible.value) {
      session?.completeShippingAddressChange({
        errors: generalError(),
        updatedPaymentRequest: buildRejectedShippingRequest()
      })
      return
    }
    if (isPickupOrder.value) {
      session?.completeShippingAddressChange({ errors: [], updatedPaymentRequest: buildCurrentPaymentRequest() })
      return
    }
    if (address.countryCode.toUpperCase() !== countryCode) {
      session?.completeShippingAddressChange({
        errors: [{ message: shippingCountryError, type: 'shippingAddressError' }],
        updatedPaymentRequest: buildRejectedShippingRequest()
      })
      return
    }
    try {
      const shippingId = cart.defaultShippingMethod?.shippingId || 'me'
      await cart.addShippingAddress([{
        ...toCartAddress(address),
        shippingId,
        subscriptions: { newsletterConsent: false }
      }], recaptcha, 'SHOPPAY_EXPRESS')
      if (!ownsSession()) return
      await cart.getApplicableShippingMethods()
      if (!ownsSession()) return
      session?.completeShippingAddressChange({
        errors: [],
        updatedPaymentRequest: buildCurrentPaymentRequest()
      })
    }
    catch (err) {
      if (!ownsSession()) return
      const unsupportedCountry = err instanceof ApiError && err.response?._data?.errorDetails?.some(
        ({ additionalInfo }: { additionalInfo?: { type?: string }[] }) => additionalInfo?.some(({ type }) => type === 'UnsupportedShippingCountryException')
      )
      session?.completeShippingAddressChange({
        errors: [{
          message: unsupportedCountry ? shippingCountryError : $t.shopPayShippingAddressError,
          type: 'shippingAddressError'
        }],
        updatedPaymentRequest: buildRejectedShippingRequest()
      })
    }
  }

  const handleDeliveryMethodChanged = async (code: string, ownsSession: () => boolean) => {
    if (!isEligible.value) {
      session?.completeDeliveryMethodChange({ errors: generalError() })
      return
    }
    if (isPickupOrder.value) {
      session?.completeDeliveryMethodChange({ updatedPaymentRequest: buildCurrentPaymentRequest() })
      return
    }
    try {
      await cart.updateShippingMethod(
        code as Parameters<typeof cart.updateShippingMethod>[0],
        'me',
        'SHOPPAY_EXPRESS'
      )
      if (!ownsSession()) return
      await checkout.prepareOrder()
      if (!ownsSession()) return
      session?.completeDeliveryMethodChange({
        updatedPaymentRequest: buildCurrentPaymentRequest()
      })
    }
    catch (err) {
      if (!ownsSession()) return
      session?.completeDeliveryMethodChange({
        errors: generalError(err instanceof Error ? err.message : undefined)
      })
    }
  }

  const handlePaymentConfirmationRequested = async (
    event: ShopPaySessionEventMap['paymentconfirmationrequested'],
    attempt: ShopPayPaymentAttempt,
    shopPaySession: ShopPayPaymentRequestSession,
    ownsCheckout: () => boolean
  ) => {
    const rejectConfirmation = (errors: ShopPayUserError[]) => {
      if (ownsCheckout() && session === shopPaySession) {
        isLoading.value = false
        checkout.isOrderProcessing = false
      }
      shopPaySession.completePaymentConfirmationRequest({ errors })
    }
    if (!isEligible.value) {
      rejectConfirmation(generalError())
      return
    }
    const billingAddress = event.billingAddress
    if (ownsCheckout() && session === shopPaySession) {
      isLoading.value = false
      orderNavigationPending = false
      checkout.isOrderProcessing = true
    }
    try {
      await checkout.prepareOrder()
      const sessionID = forterSessionToken.value || $deviceFingerPrint.getSessionId()
      const captchaResponse = await recaptcha.execute('placeOrder')
      if (!isEligible.value || !ownsCheckout()
        || session !== shopPaySession || activeShopPaySession !== shopPaySession) {
        throw new Error($t.paymentAuthenticationError)
      }
      const orderResponse = await api.cart.$placeOrder({
        billingAddress: toCartAddress(billingAddress),
        cartId: cart.basketId,
        paymentMethod: {
          additionalData: [{ paymentData: event.paymentMethod, sessionID }],
          code: 'SHOP_PAY'
        },
        recaptcha_response: captchaResponse
      })

      const paymentInstrumentId
        = (orderResponse as Order).paymentMethod?.find(({ code }) => code === 'SHOP_PAY')?.additionalData?.payment_instrument_id

      if (!orderResponse.orderNumber || !paymentInstrumentId) throw new Error($t.paymentAuthenticationError)
      const orderNumber = String(orderResponse.orderNumber)
      attempt.orderNumber = orderNumber
      attempt.paymentInstrumentId = paymentInstrumentId
      shopPaySession.completePaymentConfirmationRequest({})
    }
    catch (err) {
      const message = err instanceof Error ? err.message : $t.paymentAuthenticationError
      if (ownsCheckout() && session === shopPaySession) {
        cart.setNotification('error', message, 'top')
        checkout.isOrderProcessing = false
      }
      shopPaySession.completePaymentConfirmationRequest({ errors: generalError(message) })
    }
  }

  const handlePaymentComplete = async (
    { isTrusted, processingStatus }: ShopPayPaymentCompletePayload,
    attempt: ShopPayPaymentAttempt,
    shopPaySession: ShopPayPaymentRequestSession,
    ownsCheckout: () => boolean
  ) => {
    attempt.paymentCompleteReceived = true
    await attempt.confirmation
    if (!attempt.orderNumber || !attempt.paymentInstrumentId) return

    if (ownsCheckout() && session === shopPaySession) {
      orderNavigationPending = true
      checkout.isOrderProcessing = true
    }
    if (activeShopPaySession === shopPaySession) closeActiveSession()
    else shopPaySession.close()
    try {
      const orderResponse = await api.cart.$patchOrder(attempt.orderNumber, {
        paymentMethod: {
          additionalData: [{
            paymentData: JSON.stringify({ isTrusted, processingStatus }),
            paymentInstrumentId: attempt.paymentInstrumentId
          }],
          code: 'SHOP_PAY'
        }
      })

      if (ownsCheckout() && session === shopPaySession) {
        checkout.success(orderResponse as Order)
        cart.reset()
        await navigateTo('/order-confirmation')
      }
    }
    catch (err) {
      if (ownsCheckout() && session === shopPaySession) {
        orderNavigationPending = false
        checkout.isOrderProcessing = false
        cart.setNotification(
          'error',
          err instanceof Error ? err.message : $t.paymentAuthenticationError,
          'top'
        )
      }
    }
  }

  const registerEventListeners = (shopPaySession: ShopPayPaymentRequestSession) => {
    let checkoutOwner = Symbol('shop-pay-checkout')
    activeShopPayCheckoutOwner = checkoutOwner
    const ownsSession = () => session === shopPaySession && activeShopPaySession === shopPaySession
    // Each operation gets UI ownership, including overlapping retries of the same unpaid order.
    // Captured ownership survives SDK close, but cannot clear a replacement operation's UI.
    const claimCheckout = () => {
      if (ownsSession()) {
        checkoutOwner = Symbol('shop-pay-checkout')
        activeShopPayCheckoutOwner = checkoutOwner
      }
      const owner = checkoutOwner
      return () => activeShopPayCheckoutOwner === owner
    }
    let paymentAttempt: ShopPayPaymentAttempt = { paymentCompleteReceived: false }
    const orders = new Map<string, ShopPayPaymentAttempt>()
    let confirmationPending = false
    shopPaySession.addEventListener('sessionrequested', () => {
      if (ownsSession()) return handleSessionRequested(ownsSession)
    })
    shopPaySession.addEventListener('deliverymethodtypechanged', ({ deliveryMethodType }) => {
      if (!ownsSession()) return
      const request = buildCurrentPaymentRequest()
      shopPaySession.completeDeliveryMethodTypeChange({
        updatedPaymentRequest: request,
        ...(deliveryMethodType !== request.selectedDeliveryMethodType ? { errors: generalError() } : {})
      })
    })
    shopPaySession.addEventListener('pickuplocationchanged', ({ pickupLocation }) => {
      if (!ownsSession()) return
      const request = buildCurrentPaymentRequest()
      const isSelectedLocation = request.pickupLocations?.some(({ code }) => code === pickupLocation.code)
      shopPaySession.completePickupLocationChange({
        updatedPaymentRequest: request,
        ...(!isSelectedLocation ? { errors: generalError() } : {})
      })
    })
    shopPaySession.addEventListener('pickuplocationfilterchanged', () => {
      if (!ownsSession()) return
      shopPaySession.completePickupLocationFilterChange({ updatedPaymentRequest: buildCurrentPaymentRequest() })
    })
    shopPaySession.addEventListener('shippingaddresschanged', ({ shippingAddress }) => {
      if (ownsSession()) return handleShippingAddressChanged(shippingAddress, ownsSession)
    })
    shopPaySession.addEventListener('deliverymethodchanged', ({ deliveryMethod }) => {
      if (ownsSession()) return handleDeliveryMethodChanged(deliveryMethod.code, ownsSession)
    })
    shopPaySession.addEventListener('paymentconfirmationrequested', (event) => {
      if (!ownsSession()) return
      if (confirmationPending) {
        shopPaySession.completePaymentConfirmationRequest({ errors: generalError() })
        return
      }
      const previousAttempt = paymentAttempt
      // A retry makes the prior decline stale until we know which order it submitted.
      paymentAttempt = {
        ...(!previousAttempt.failure && !previousAttempt.paymentCompleteReceived && {
          orderNumber: previousAttempt.orderNumber,
          paymentInstrumentId: previousAttempt.paymentInstrumentId
        }),
        paymentCompleteReceived: false
      }
      const attempt = paymentAttempt
      if (attempt.orderNumber) orders.set(attempt.orderNumber, attempt)
      confirmationPending = true
      paymentAttempt.confirmation = handlePaymentConfirmationRequested(
        event,
        paymentAttempt,
        shopPaySession,
        claimCheckout()
      ).then(() => {
        // A replacement order does not supersede the earlier order's explicit decline.
        if (previousAttempt.orderNumber && previousAttempt.orderNumber !== attempt.orderNumber) {
          orders.set(previousAttempt.orderNumber, previousAttempt)
        }
        if (attempt.orderNumber) orders.set(attempt.orderNumber, attempt)
      }).finally(() => {
        confirmationPending = false
      })
      return paymentAttempt.confirmation
    })
    shopPaySession.addEventListener('paymentcomplete', (event) => {
      paymentAttempt.completion ??= handlePaymentComplete(event, paymentAttempt, shopPaySession, claimCheckout())
      return paymentAttempt.completion
    })
    shopPaySession.addEventListener('paymentattemptfailed', ({ error }) => {
      if (paymentAttempt.paymentCompleteReceived) return
      paymentAttempt.lastError = error
      if (!ownsSession() || activeShopPayCheckoutOwner !== checkoutOwner) return
      cart.setNotification('error', error.reason || $t.paymentAuthenticationError, 'top')
      isLoading.value = false
      orderNavigationPending = false
      checkout.isOrderProcessing = false
    })
    shopPaySession.addEventListener('windowclosed', async () => {
      // Reopening must create a new session so delayed receipts keep their original order.
      if (activeShopPaySession === shopPaySession) activeShopPaySession = undefined
      const attempt = paymentAttempt
      const owner = checkoutOwner
      const ownsCheckout = () => activeShopPayCheckoutOwner === owner
      if (ownsCheckout()) {
        checkoutValidationSucceeded = false
        isLoading.value = false
      }
      await attempt.confirmation
      await Promise.all([...orders.values()].map((order) => {
        if (order.failure) return order.failure
        const { orderNumber, paymentInstrumentId, lastError } = order
        // canvas-debt: uncertain closure cannot release reserved funds, add backend receipt reconciliation before enabling close-only cancellation [GLOBAL15-133195]
        if (!orderNumber || !paymentInstrumentId || order.paymentCompleteReceived || !lastError?.errorCode) {
          return undefined
        }
        if (ownsCheckout()) checkout.isOrderProcessing = true
        order.failure = (async () => {
          try {
            await api.cart.$patchOrder(orderNumber, {
              paymentMethod: {
                additionalData: [{
                  paymentData: JSON.stringify({
                    errorCode: lastError.errorCode,
                    ...(lastError.reason && { reason: lastError.reason })
                  }),
                  paymentInstrumentId
                }],
                code: 'SHOP_PAY'
              }
            })
          }
          catch (err) {
            if (ownsCheckout()) {
              cart.setNotification('error', err instanceof Error ? err.message : $t.paymentAuthenticationError, 'top')
            }
          }
        })()
        return order.failure
      }))
      if (ownsCheckout() && !orderNavigationPending) checkout.isOrderProcessing = false
    })
  }

  const activateSession = (reopen = false) => {
    const paymentRequestApi = window.ShopPay!.PaymentRequest
    if (!reopen && session && activeShopPaySession === session) return

    closeActiveSession()
    const paymentRequest = buildCurrentPaymentRequest()
    session = paymentRequestApi.createSession({ paymentRequest })
    activeShopPaySession = session
    registerEventListeners(session)
  }

  const handleButtonClick = (event: Event) => {
    try {
      checkoutValidationSucceeded = false
      if (!isEligible.value || (beforeProceed && !beforeProceed())) {
        event.preventDefault()
        event.stopImmediatePropagation()
        return
      }
      // The SDK resolves its singleton session inside its own click handler.
      // Capture first so multiple rendered Shop Pay buttons activate the correct surface.
      activateSession(context === 'checkout-payment')
      checkoutValidationSucceeded = true
    }
    catch {
      event.preventDefault()
      event.stopImmediatePropagation()
      checkoutValidationSucceeded = false
      isLoading.value = false
      cart.setNotification('error', $t.paymentAuthenticationError, 'top')
    }
  }

  const initialize = async (
    selector: string,
    { buyWith = false }: { buyWith?: boolean } = {}
  ) => {
    const generation = ++initializationGeneration
    if (!isEligible.value || !isClient()) return false

    if (!window.ShopPay) await loadScript()
    if (context === 'checkout-payment'
      && (generation !== initializationGeneration || !isEligible.value)) {
      return false
    }
    if (!window.ShopPay) throw new Error('Shop Pay SDK did not load')

    window.ShopPay.PaymentRequest.configure({
      clientId: shopPayConfig.value.clientId,
      debug: shopPayConfig.value.debug,
      discountCodeField: false,
      locale: locale.split('-')[0],
      shopId: shopPayConfig.value.shopId
    })

    activateSession()
    await window.ShopPay.PaymentRequest.createButton({ buyWith }).render(selector)
    if (context === 'checkout-payment'
      && (generation !== initializationGeneration || !isEligible.value)) {
      return false
    }
    buttonElement?.removeEventListener('click', handleButtonClick, true)
    buttonElement = document.querySelector(selector) || undefined
    buttonElement?.addEventListener('click', handleButtonClick, true)
    return true
  }

  const destroy = () => {
    initializationGeneration++
    buttonElement?.removeEventListener('click', handleButtonClick, true)
    buttonElement = undefined
    checkoutValidationSucceeded = false
    isLoading.value = false
    if (isClient() && activeShopPaySession === session) {
      closeActiveSession()
    }
    session = undefined
  }

  return {
    destroy,
    initialize,
    isEligible,
    isLoading: readonly(isLoading)
  }
}
